import { useState } from "react";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { store } from "@store";
import { TICKET_SLIDER, TicketCreateLocation } from "@navigation";
import { CreateTicketData } from "@tickets/TicketCreate/CreateTicketData";
import { TicketCreatePageProps } from "@tickets/TicketCreate/TicketCreatePage";
import {
  ConsultantEventLog_GetEvents,
  ConsultantQualityCheckConsultantDataFragment,
  ConsultantQualityCheckDataFragment,
  ConsultantQualityItemEditor_SetRequestSent,
  ConsultantQuality_ConsultantNewFragment,
  ConsultantQuality_GetConsultantNew,
  TodoInput
} from "@bonliva-crm/graphql";

export function useRequestSent({
  data,
  consultantData,
  requestFn,
  ticketProps = {},
  oldDesign = false
}: {
  data: ConsultantQualityCheckDataFragment;
  consultantData:
    | ConsultantQuality_ConsultantNewFragment
    | ConsultantQualityCheckConsultantDataFragment;
  requestFn?: (values?: TodoInput) => Promise<any>;
  ticketProps?: Omit<TicketCreatePageProps, "onCreated" | "onCancel">;
  oldDesign?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const [ordered, setOrdered] = useState(false);
  const snackbar = useSnackbarsPipeline({ old: oldDesign });

  const setRequestSentFunc = async (values?: TodoInput) => {
    console.log("set request sent", values);
    if (requestFn?.call) {
      await requestFn(values);
    } else {
      const options = {
        refetchQueries: [
          ConsultantEventLog_GetEvents.OperationName,
          ConsultantQuality_GetConsultantNew.OperationName
        ]
      };

      await ConsultantQualityItemEditor_SetRequestSent.func(
        {
          consultantId: consultantData._id,
          country: data.Country,
          qualityCheckName: data.Name,
          setOn: new Date(),
          todo: values
        },
        options
      );
    }
    setOrdered(true);

    snackbar.showSuccess(locale.Ordered);
  };

  const setRequestSent = () => {
    return new Promise((resolve, reject) => {
      const itemName = data.Name;

      const ticketCreateData = CreateTicketData.ByQualificationName(
        consultantData,
        data.Name
      );

      store().navigation.open(
        TICKET_SLIDER,
        new TicketCreateLocation({
          itemName,
          initialValues: ticketCreateData,
          withReference: false,
          withCriminalRecord: false,
          consultantId: consultantData._id,
          descriptionRequired: false,
          dueDateRequired: false,
          showSuccessAlert: false,
          onCreated: (todoInput, values) => {
            return setRequestSentFunc(todoInput).then(() => {
              resolve(null);
            }, reject);
          },
          onCancel: () => resolve(null),
          disabledList: ["assignedTeam"],
          ...ticketProps,
          dueDateProps: {
            disabled: ticketCreateData.dueDateDisabled,
            ...(ticketProps.dueDateProps || {}),
            DatePickerProps: {
              helperText: ticketCreateData.dueDateText || "",
              ...(ticketProps.dueDateProps?.DatePickerProps || {})
            }
          }
        })
      );

      store().navigation.on("close", TICKET_SLIDER, () => {
        resolve(null);
      });
    });
  };

  const onRequestSent = (withTicket = true) => {
    setLoading(true);

    (withTicket ? setRequestSent() : setRequestSentFunc())
      .catch((e) => {
        if (e === "ticket_cancel") {
          return;
        }

        console.error(e);
        snackbar.showError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return { loading, ordered, onRequestSent };
}
